// オブジェクト単体のドラッグ移動ハンドラ。
//
// 振る舞い:
// - pointerdown: 単一選択（selectedObjectIds = [id]）し、ドラッグ追跡を開始する。
//   stage 側へのイベント伝播は止めて、空白部のパン開始と競合しないようにする。
// - pointermove: ドラッグ中はオブジェクト要素の transform を直接書き換えてプレビューする
//   （doc store / 共同編集サーバへは送らないため、他者には見えないが UX は軽快）。
// - pointerup: ドラッグ距離が閾値以上なら、最終位置を transformObject op として永続化する。
//   閾値未満（=ただのクリック）なら何もしない。
//
// 編集中（contentEditable のテキスト入力）はドラッグを開始しない（pointerdown を見送る）。

import { useEffect, useRef, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";

interface Options {
  readonly objectId: string;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly dispatcher: CanvasOpDispatcher;
  readonly initialX: number;
  readonly initialY: number;
  readonly initialRotation?: number | undefined;
  /** 編集中はドラッグを抑止する（テキスト編集中など）。 */
  readonly disabled?: boolean | undefined;
}

const CLICK_THRESHOLD_PX = 3;

interface DragState {
  startClientX: number;
  startClientY: number;
  baseLeft: number;
  baseTop: number;
  baseTransform: string;
  moved: boolean;
}

export function useObjectDrag(options: Options): void {
  const { objectId, elementRef, dispatcher, initialX, initialY, initialRotation, disabled } =
    options;

  // initialX / initialY が effect の依存に含まれるため、他者編集や自分の op.commit
  // エコーで props が更新されるたびに effect が再走する。dragState を effect の
  // closure ローカルに置くと、再走時にドラッグが「中断」されてしまうため、
  // 親 hook 全体で永続する ref に格納しておく。
  const dragStateRef = useRef<DragState | null>(null);
  // initialX/Y も ref 化しておき、最新の prop 値を listener から参照できるようにする。
  // これにより effect の依存から initialX/Y を外して再走を抑制できる。
  const baseRef = useRef({ x: initialX, y: initialY });
  baseRef.current = { x: initialX, y: initialY };

  useEffect(() => {
    if (disabled) return undefined;
    const el = elementRef.current;
    if (!el) return undefined;

    const onPointerDown = (event: PointerEvent): void => {
      // 編集対象がコンテナ自体ではない（contentEditable などへの transit）の場合はスキップ。
      const target = event.target as HTMLElement | null;
      if (target && target !== el && target.isContentEditable) return;
      // 主ボタン以外（右クリック等）はドラッグしない
      if (event.button !== 0) return;

      // 単一選択
      useCanvasStore.getState().setSelectedObjectIds([objectId]);

      dragStateRef.current = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        baseLeft: baseRef.current.x,
        baseTop: baseRef.current.y,
        baseTransform: el.style.transform,
        moved: false,
      };
      el.setPointerCapture(event.pointerId);
      // stage 側のパン開始へ届かないようにする
      event.stopPropagation();
    };

    const onPointerMove = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const distance = Math.hypot(
        event.clientX - dragState.startClientX,
        event.clientY - dragState.startClientY
      );
      // 閾値未満の手ブレでは left/top を一切書き換えない（クリックの意図を保護する）。
      if (!dragState.moved && distance <= CLICK_THRESHOLD_PX) return;
      dragState.moved = true;

      const view = useCanvasStore.getState().view;
      // クライアント座標差分はステージ座標では zoom で割る必要がある
      const dx = (event.clientX - dragState.startClientX) / view.zoom;
      const dy = (event.clientY - dragState.startClientY) / view.zoom;
      // CSS で位置を即時反映（DOM の left/top を直接動かしてプレビュー）
      el.style.left = `${dragState.baseLeft + dx}px`;
      el.style.top = `${dragState.baseTop + dy}px`;
    };

    const onPointerUp = (event: PointerEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState) return;
      const view = useCanvasStore.getState().view;
      const dx = (event.clientX - dragState.startClientX) / view.zoom;
      const dy = (event.clientY - dragState.startClientY) / view.zoom;
      const moved = dragState.moved;
      dragStateRef.current = null;
      if (el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      if (moved) {
        // 最終位置を doc store / サーバへ反映
        dispatcher.transformObject(objectId, {
          x: dragState.baseLeft + dx,
          y: dragState.baseTop + dy,
        });
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
    // 注意: initialX / initialY / initialRotation を依存から外している。最新値は baseRef
    // 経由で listener から参照するため、effect 再走による listener 張り直しは不要。
    // （ドラッグ中に他者編集で props が変動しても自分のドラッグが中断されないようにする。）
  }, [objectId, elementRef, dispatcher, disabled]);
}
